import React from 'react'

import { Dashboard, PageLayout } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  Dropdown,
  Fieldset,
  Loading,
  Subsection,
  Tag,
  ToolbarButton,
} from '@fourthwall/components'
import { ArrowShortDownIcon, DotsHorizontalIcon } from '@fourthwall/icons'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { isPaidTier } from '../../utils'
import { TierSkeleton } from './TierSkeleton'
import { useTierContext } from './hooks/useTierContext'
import { Analytics } from './modules/Analytics'
import { AskForShippingAddress } from './modules/AskForShippingAddress'
import { Description } from './modules/Description'
import { Discount } from './modules/Discount'
import { DiscountType } from './modules/DiscountType'
import { Image } from './modules/Image'
import { InternationalPricing } from './modules/InternationalPricing'
import { MonthDiscount } from './modules/MonthDiscount'
import { Price } from './modules/Price'
import { TimeLimit } from './modules/TimeLimit'
import { Title } from './modules/Title'
import { YearDiscount } from './modules/YearDiscount'
import { TierProvider } from './providers/TierProvider'

const TierContent = () => {
  const {
    tier,
    form,
    isLoading,
    isPriceRatesLoading,
    publishTier,
    unpublishTier,
    deleteTier,
    deleteFreeTier,
  } = useTierContext()

  if (!tier) return <TierSkeleton />

  return (
    <Loading isLoading={isLoading}>
      <PageLayout
        leftContent={
          <>
            <Subsection title="Tier details">
              <Fieldset>
                <Title />
                <Description />
                <Image />
              </Fieldset>
            </Subsection>
            <Subsection title="Pricing">
              <Fieldset>
                <Price />
                <InternationalPricing />
                <Discount />
                <DiscountType />
                <MonthDiscount />
                <YearDiscount />
                <TimeLimit />
              </Fieldset>
            </Subsection>
            {isPaidTier(tier) && (
              <Subsection title="Advanced">
                <AskForShippingAddress />
              </Subsection>
            )}
          </>
        }
        rightContent={<Analytics />}
      >
        <Dashboard.Section
          name={tier.name}
          topContent={<BackButton label="Perks & tiers" to={routing.memberships.perks.self} />}
          toolbarContent={
            <>
              {isPaidTier(tier) && (
                <Dropdown
                  content={
                    <Dropdown.Item
                      label={tier.status === 'Published' ? 'Unpublish' : 'Publish'}
                      onClick={tier.status === 'Published' ? unpublishTier : publishTier}
                    />
                  }
                  position="bottom-end"
                >
                  <div>
                    <Tag
                      label={tier?.status === 'Published' ? 'Published' : 'Unpublished'}
                      appearance={tier?.status === 'Published' ? 'success' : 'default'}
                      icon={ArrowShortDownIcon}
                      hoverable
                      size="large"
                    />
                  </div>
                </Dropdown>
              )}

              <Dropdown
                content={
                  <Dropdown.Item
                    label="Delete"
                    onClick={isPaidTier(tier) ? deleteTier : deleteFreeTier}
                  />
                }
                position="bottom-end"
              >
                <ToolbarButton label="Settings" icon={DotsHorizontalIcon} />
              </Dropdown>
            </>
          }
          titleSpacing="medium"
          bottomMargin="large"
        />
      </PageLayout>

      <LeavingGuard when={form.dirty} isValid={form.isValid} onSave={form.submitForm} />

      <ConfirmBar
        isOpen={form.dirty}
        isLoading={isLoading || isPriceRatesLoading}
        onConfirm={form.submitForm}
        onCancel={form.resetForm}
      />
    </Loading>
  )
}

export const Tier = () => {
  return (
    <TierProvider>
      <TierContent />
    </TierProvider>
  )
}
